<template>
  <div :class="bgClass" style="width: 100%; height: 100%">
    <div v-if="loading">
      <div
        style="
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 20px;
          width: 100%;
        "
      >
        <loading-skeleton style="width: 33%; height: 3.25em" />
        <loading-skeleton style="width: 66%; height: 2em" />
        <loading-skeleton style="width: 100%; height: 480px" />
      </div>
    </div>

    <div
      v-if="isPreviewEnabled && !loading"
      style="position: relative; width: 100%"
    >
      <Button
        v-tooltip="tooltip"
        icon-name="info"
        icon-type="icon-only"
        :style-type="matchesAudience ? 'remarkable' : 'none'"
        size="large"
        style="position: absolute; right: 10px; top: 10px"
        @click="
          () => {
            if (bgClass === '') {
              bgClass = 'experience__highlighted';
            } else {
              bgClass = '';
            }
          }
        "
      />
    </div>
    <div v-if="matchesAudience">
      <MappedComponents
        :items="item.fields.audienceContent ?? []"
        :color-mode="props.colorMode"
      />
    </div>
    <div :style="style">
      <MappedComponents
        v-if="!matchesAudience && displayBaselineContent"
        :items="item.fields.baselineContent ?? []"
        :color-mode="props.colorMode ?? $colorMode"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { IExperience } from '~/lib/ContentfulService';
import { LoadingSkeleton, Button } from '@hypercodestudio/basler-components';
import type { ColorMode } from '@hypercodestudio/basler-components/dist/models/ColorMode';

const { $colorMode } = useNuxtApp();

const { $textDictionary } = useNuxtApp();
const {
  public: { isPreviewEnabled }
} = useRuntimeConfig();

interface Props {
  item: IExperience;
  colorMode?: ColorMode;
  displayBaselineContent?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  displayBaselineContent: true,
  colorMode: 'light'
});

const tooltip = computed(() => {
  if (
    props.item.fields.audiences === undefined ||
    props.item.fields.audiences.length == 0
  ) {
    return (
      $textDictionary.value['experiences.preview.widget.tooltip.noaudiences'] ??
      'No Audiences configured'
    );
  }

  return {
    escape: false,
    value: `${
      $textDictionary.value['experiences.preview.widget.tooltip.header'] ??
      'At least one Audience must be true'
    }:
  <ul><li>${props.item.fields.audiences
    ?.map((audience) => {
      return `<div style="color: ${
        $matchingAudiences.value.includes(audience.sys.id)
          ? 'lightgreen'
          : '#FFCCCB'
      };">${audience.fields.internalName}</div>`;
    })
    .join('</li><li>')}</li>`
  };
});

const bgClass = ref<string>('');
const loading = computed(() => import.meta.server);
const style = ref<string>('display: none;');
const { $matchingAudiences } = useNuxtApp();
const matchesAudience = computed(() => {
  if (import.meta.server) {
    return false;
  }

  const ids = props.item.fields.audiences?.map((a) => a.sys.id) ?? [];
  for (const id of ids) {
    if ($matchingAudiences.value.includes(id)) {
      return true;
    }
  }
  return false;
});

onMounted(() => {
  style.value = '';
});
</script>

<style>
.experience__highlighted {
  border: 5px solid #d7fd93;
  border-radius: 5%;
}
</style>
